import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "January 3, 2026";

  return (
    <div data-testid="privacy-policy-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F7] mb-4">
            Privacy Policy
          </h1>
          <p className="text-[#71717A]">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#050505]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-[#A1A1AA] leading-relaxed text-lg">
                  At Eleven11 Studios ("we," "our," or "us"), we are committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you visit our website www.eleven11-studios.com or engage with our web design and 
                  development services.
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">1. Information We Collect</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  We collect information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Fill out contact forms on our website</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Request a consultation for web design services</li>
                  <li>Engage our website development services</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  <strong className="text-[#F5F5F7]">Personal Information collected may include:</strong>
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4 mt-2">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Company/Business name</li>
                  <li>Business address (when applicable)</li>
                  <li>Payment information (processed securely through third-party processors)</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">2. Cookies and Analytics</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to enhance your experience on our website. These include:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li><strong className="text-[#F5F5F7]">Essential Cookies:</strong> Required for basic website functionality</li>
                  <li><strong className="text-[#F5F5F7]">Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                  <li><strong className="text-[#F5F5F7]">Functional Cookies:</strong> Remember your preferences for a better experience</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  You can control cookie preferences through your browser settings. Disabling certain cookies may affect website functionality.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">3. How We Use Your Information</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Deliver our web design and development services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send promotional communications (with your consent)</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">4. Data Sharing and Third Parties</h2>
                <div className="bg-[#111111] border border-[#D4AF37]/30 p-6 mb-4">
                  <p className="text-[#F5F5F7] font-semibold text-lg">
                    We do NOT sell, trade, or rent your personal information to third parties.
                  </p>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li><strong className="text-[#F5F5F7]">Service Providers:</strong> Trusted third-party vendors who assist in operating our business (payment processors, hosting providers)</li>
                  <li><strong className="text-[#F5F5F7]">Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong className="text-[#F5F5F7]">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">5. Data Security</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over 
                  the Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">6. Your Rights</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt-out of promotional communications</li>
                  <li>Withdraw consent where applicable</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">7. Third-Party Links</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                  or content of these external sites. We encourage you to review the privacy policies of any third-party 
                  sites you visit.
                </p>
              </div>

              {/* Section 8 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">8. Children's Privacy</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a minor, please contact us immediately.
                </p>
              </div>

              {/* Section 9 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">9. Changes to This Policy</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an 
                  updated revision date. We encourage you to review this policy periodically.
                </p>
              </div>

              {/* Contact Section */}
              <div className="bg-[#111111] border border-white/10 p-8 mt-12">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">10. Contact Us</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                  please contact us at:
                </p>
                <div className="space-y-3">
                  <p className="text-[#F5F5F7] font-semibold">Eleven11 Studios</p>
                  <p className="text-[#A1A1AA] text-sm">22-23, Block S, DLF Phase 3, Gurugram, Haryana, India 122002</p>
                  <a 
                    href="mailto:hello@eleven11-studios.com" 
                    className="flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    hello@eleven11-studios.com
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
